import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router";
import { useAuth } from "@/contexts/AuthContext";
import { HandInWork } from "@/components/session/handin/HandInWork";
import { AssignmentDraftGuard } from "../assignment-draft-guard";
import { WorkModelSink } from "./WorkBoxHost";
import type { Box } from "@/components/ui/box.schema";
import { assignmentsApi } from "@/lib/api/assignments";
import { courseHomeApi } from "@/lib/api/courseHome";
import { ApiError } from "@/lib/api/client";
import type { VisitLevel } from "@/lib/visit/address";
import type { VisitResolution } from "@/lib/visit/registry";
import { isHistoricalTerm } from "@/lib/term-state";

const denied = (error: unknown) => error instanceof ApiError && [401, 403, 404].includes(error.status);

/** Response and exact receipt share the canonical learner controller. The
 * collector carries models only: private drafts, commit keys and recovery stay
 * in HandInWork and its existing writers. Memoized controller elements keep
 * publishing a model from feeding back into another controller render. */
export function useResponseVisit(level: VisitLevel | null): VisitResolution | null {
  const { me } = useAuth();
  const { pathname } = useLocation();
  const permitted = Boolean(level && me?.workspaces.learning.available);
  const scope = `${me?.tenant_id}:${me?.user_id}`;
  const identity = `${scope}:${level?.object}:${level?.id}:${level?.job ?? ""}`;
  const courseInPath = pathname.match(/\/courses\/([^/]+)/)?.[1];
  const assignment = useQuery({ queryKey: ["assignment", level?.id, "detail"], queryFn: () => assignmentsApi.get(level!.id), enabled: permitted, retry: false });
  const courseId = assignment.data?.course_id;
  const course = useQuery({ queryKey: ["response-course", scope, courseId], queryFn: () => courseHomeApi.getLearningContext(courseId!), enabled: permitted && Boolean(courseId) && !denied(assignment.error), retry: false });
  const attempts = useQuery({ queryKey: ["assignment", level?.id, "submissions"], queryFn: () => assignmentsApi.listSubmissions(level!.id), enabled: permitted && Boolean(assignment.data) && !denied(assignment.error), retry: false });
  const [published, setPublished] = useState<{ identity: string; box: Box | null } | null>(null);
  const publish = useCallback((_id: string, box: Box | null) => {
    setPublished(previous => previous?.identity === identity && previous.box === box ? previous : { identity, box });
  }, [identity]);
  const exact = level?.object === "receipt" ? level.job : null;
  const refused = !permitted || denied(assignment.error) || denied(course.error) || denied(attempts.error)
    || assignment.data && (assignment.data.id !== level?.id || assignment.data.status === "draft" || courseInPath && courseInPath !== courseId)
    || course.data && course.data.course.id !== courseId
    || attempts.data && (attempts.data.some(attempt => attempt.assignment_id !== level?.id || attempt.course_id !== courseId)
      || exact && course.data && !attempts.data.some(attempt => attempt.id === exact && attempt.enrollment_id === course.data?.relationship.enrollment_id));
  const ready = Boolean(!refused && assignment.data && course.data && attempts.data);
  const termOpen = Boolean(course.data && (course.data.relationship.enrollment_status ?? "enrolled") === "enrolled" && course.data.course.status !== "archived" && !isHistoricalTerm(course.data.course.term?.status));
  const controller = useMemo(() => ready && assignment.data && course.data && attempts.data ? (
    <AssignmentDraftGuard draftLabel="Your unsaved response" confirmMessage="Leave this response with unsaved changes?">
      <WorkModelSink publish={publish}>
        <HandInWork key={identity} assignmentId={assignment.data.id} title={assignment.data.title}
          timezone={course.data.course.timezone ?? "UTC"} termOpen={termOpen} termName={course.data.relationship.enrollment_status && course.data.relationship.enrollment_status !== "enrolled" ? "Current participation in this course" : course.data.course.status === "archived" ? "This archived course" : course.data.course.term?.name}
          pastDue={assignment.data.past_due ?? false} filled selectedAttemptId={exact ?? undefined}
          initialTerms={assignment.data} initialAttempts={attempts.data} enrollmentId={course.data.relationship.enrollment_id} />
      </WorkModelSink>
    </AssignmentDraftGuard>
  ) : null, [ready, assignment.data, course.data, attempts.data, publish, identity, termOpen, exact]);
  if (!level) return null;
  if (refused) return { status: "refused" };
  const retry = () => void Promise.all([assignment.refetch(), courseId ? course.refetch() : null, attempts.refetch()]);
  const box = ready && published?.identity === identity ? published.box : null;
  const id = `hand-in-${level.id}`;
  const shown: Box | null = box && course.isError ? {
    ...box, read: { status: "stale", last: null, readAt: new Date(course.dataUpdatedAt).toISOString(), sentence: "The course context could not refresh. Your response stays here.", refresh: () => void course.refetch() },
    ...(box.kind === "task" ? { commit: { ...box.commit, blockers: () => [{ kind: "missing" as const, part: "brief", sentence: "Read the current course context before handing in." }] } } : {}),
  } as Box : box;

  return {
    status: "ready", subject: ready ? assignment.data!.title : "Response", controller,
    boxes: shown ? [shown.kind === "task" ? { ...shown, footer: "host" } : shown] : [{
      kind: "fact", id, label: "Response", job: "Read this exact learner response", owner: { system: "imam" }, fold: "never", detail: () => "",
      read: assignment.isError || course.isError || attempts.isError ? { status: "failed", sentence: "This response could not be read.", retry } : { status: "loading" },
    }], opens: [id],
  };
}
